// src/components/MessagesDrawer.js
import React, { useEffect, useState, useRef } from 'react';
import {
  Drawer, Box, Typography, IconButton, List, ListItem, ListItemText,
  TextField, Button, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ChatModal from './ChatModal';
import { http } from "../lib/axios";
import { useChat } from '../context/ChatContext';

// Helper to normalize media URLs
function toRelativeMedia(url) {
  if (!url) return null;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/media/')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url.replace(/^https?:\/\/[^/]+/, '');
  }
  const trimmed = url.replace(/^\.?\/*/, '');
  return trimmed.startsWith('media/') ? `/${trimmed}` : `/media/${trimmed}`;
}

export default function MessagesDrawer({ open, onClose }) {
  const { chatRecipient, setChatRecipient } = useChat();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [authUserId, setAuthUserId] = useState(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [recipient, setRecipient] = useState(null);

  const messagesEndRef = useRef(null);

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    const fetchUserId = async () => {
      if (token) {
        try {
          const { data } = await http.get('/api/accounts/me/');
          setAuthUserId(data.id);
        } catch (err) {
          console.error('Failed to fetch user ID', err);
        }
      }
    };
    fetchUserId();
  }, [token]);

  useEffect(() => {
    if (open) {
      http.get('/messages/').then(res => setConversations(res.data));
      http.get('/notifications/').then(res => setNotifications(res.data));
    }
  }, [open]);

  useEffect(() => {
    if (!open || !chatRecipient?.id) return;

    setRecipient(chatRecipient);
    http.post('/messages/start/', { recipient_id: chatRecipient.id })
      .then((res) => {
        setConversations((prev) => {
          const exists = prev.some((conv) => conv.id === res.data.id);
          return exists ? prev : [res.data, ...prev];
        });
        loadMessages(res.data);
      })
      .catch((err) => {
        console.error('Failed to launch conversation from shortcut', err);
      })
      .finally(() => {
        setChatRecipient(null);
      });
  }, [open, chatRecipient, setChatRecipient]);

  const loadMessages = (conv) => {
    setSelectedConv(conv);
    setChatModalOpen(true);
    http.get(`/messages/${conv.id}/messages/`)
      .then(res => {
        setMessages(res.data);
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
              behavior: 'instant',
              block: "end",
              inline: "nearest"
            });
          }
        }, 100);
      });
  };

  const sendMessage = () => {
    if (!newMsg.trim()) return;
    http.post(`/messages/${selectedConv.id}/send/`, { content: newMsg })
      .then(res => {
        const updatedMessages = [...messages, res.data];
        setMessages(updatedMessages);
        setNewMsg('');
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
              behavior: 'instant',
              block: "end",
              inline: "nearest"
            });
          }
        }, 100);
      });
  };

  const handleSearch = () => {
    http.get(`/api/accounts/search/?username=${searchUser}`)
      .then(res => setSearchResults(res.data));
  };

  const startConversation = (user) => {
    setRecipient(user);
    const recipientId = user.id;
    http.post(`/messages/start/`, { recipient_id: recipientId })
      .then(res => {
        setConversations([res.data, ...conversations]);
        setOpenDialog(false);
        loadMessages(res.data);
      });
  };

  const confirmDelete = (convId) => {
    setConfirmDeleteId(convId);
  };

  const handleDeleteConfirmed = () => {
    const convId = confirmDeleteId;
    setConfirmDeleteId(null);
    if (selectedConv?.id === convId) {
      setChatModalOpen(false);
      setSelectedConv(null);
      setMessages([]);
    }
    http.delete(`/messages/${convId}/`)
      .then(() => {
        setConversations(conversations.filter(c => c.id !== convId));
      })
      .catch(err => console.error('Failed to delete conversation', err));
  };

  const deleteNotification = (notifId) => {
    http.delete(`/notifications/${notifId}/`)
      .then(() => {
        setNotifications(notifications.filter(n => n.id !== notifId));
      })
      .catch(err => console.error('Failed to delete notification', err));
  };

  const getOtherParticipant = (conv) =>
    conv.participants.find(p => p.id !== authUserId) || conv.participants[0];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ width: 400, flexShrink: 0 }}
      PaperProps={{
        sx: {
          top: 'var(--navbar-height)',
          height: 'calc(100% - var(--navbar-height))',
          width: 400,
        },
      }}
    >
      <Box sx={{ width: 400, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Messages</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>

        <Button variant="contained" fullWidth onClick={() => setOpenDialog(true)} sx={{ mb: 2 }}>
          New Conversation
        </Button>

        <Divider sx={{ mb: 1 }} />
        <Typography variant="subtitle1">Your Conversations:</Typography>

        <List>
          {conversations.map(conv => {
            const other = getOtherParticipant(conv);

            // Derive display name
            const otherName = other?.first_name
              ? `${other.first_name} ${other.last_name}`
              : other?.username || 'User';

            // Try to resolve primary profile image on the participant
            let rawPrimary =
              other?.primary_profile_image ||
              other?.user?.primary_profile_image ||
              null;

            if (!rawPrimary && Array.isArray(other?.profile_images)) {
              const primaryFromList =
                other.profile_images.find(p => p.is_primary)?.url ||
                other.profile_images[0]?.url;
              if (primaryFromList) rawPrimary = primaryFromList;
            }

            const avatarSrc = rawPrimary ? toRelativeMedia(rawPrimary) : null;

            return (
              <ListItem
                button
                key={conv.id}
                onClick={() => {
                  setRecipient(other);
                  loadMessages(conv);
                }}
                secondaryAction={
                  <IconButton edge="end" onClick={() => confirmDelete(conv.id)}>
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  {avatarSrc ? (
                    <Avatar
                      src={avatarSrc}
                      alt={otherName}
                      sx={{ width: 40, height: 40 }}
                      imgProps={{ referrerPolicy: 'no-referrer' }}
                    />
                  ) : (
                    <Avatar sx={{ width: 40, height: 40 }}>
                      {otherName.charAt(0)}
                    </Avatar>
                  )}

                  <ListItemText
                    primary={
                      <Typography component="span" fontWeight={600}>
                        {otherName}
                      </Typography>
                    }
                    secondary={conv.latest_message?.content || 'No messages'}
                  />
                </Box>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1">Your Notifications:</Typography>
        <List>
          {notifications.map(notif => (
            <ListItem
              key={notif.id}
              secondaryAction={
                <IconButton edge="end" onClick={() => deleteNotification(notif.id)}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText primary={notif.message} />
            </ListItem>
          ))}
        </List>

        {/* New Conversation dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Start New Conversation</DialogTitle>
          <DialogContent>
            <TextField
              label="Search by Username"
              onChange={e => setSearchUser(e.target.value)}
              value={searchUser}
              fullWidth
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              sx={{ mb: 2 }}
            />
            {searchResults.map(user => (
              <ListItem button key={user.id} onClick={() => startConversation(user)}>
                <ListItemText
                  primary={
                    <strong>
                      {user.first_name} {user.last_name} ({user.username})
                    </strong>
                  }
                />
              </ListItem>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this conversation?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
            <Button color="error" onClick={handleDeleteConfirmed}>Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Chat modal */}
        <ChatModal
          open={chatModalOpen}
          onClose={() => setChatModalOpen(false)}
          conversation={selectedConv}
          messages={messages}
          onSend={sendMessage}
          newMsg={newMsg}
          setNewMsg={setNewMsg}
          authUserId={authUserId}
          messagesEndRef={messagesEndRef}
          recipient={recipient}
        />

      </Box>
    </Drawer>
  );
}
